import React, { ErrorInfo, FC, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { FinancialCalcReversalResult } from '../api/selfApi'
import '../assets/css/highchartsContainer.css'
import { makeChart, resetChart, toggleSeriesVisibility } from '../js/financialCalcReversalChart'
import { AppState } from '../store'
import { FinancialCalcReversalResultComponent, HoverPayload } from './FinancialCalcReversalResult'

const chartId = 'financialCalcReversalChart'

export interface ErrorBoundaryProps {
  notifyError?: (error: Error, info: ErrorInfo) => void
}

interface FinancialCalcReversalChartProps {
  isResponsive: boolean
  isMonthlyWithdrawal: boolean
  disSavingRate: number | string | null
  addition: number | string | null
}

interface ChartSeries {
  type: string
  name: string
  key: string
  color: string
  dashStyle: string
  data: { x: number; y: number | null }[]
}

function makeChartSeries(result: FinancialCalcReversalResult): ChartSeries[] | null {
  const zeroIndexOfRateInvestment = result.rate?.investmentList.findIndex((x) => x.amount < 1) ?? -1
  const zeroIndexOfRateDeposit = result.rate?.depositList.findIndex((x) => x.amount < 1) ?? -1
  const zeroIndexOfFixedInvestment =
    result.fixed?.investmentList.findIndex((x) => x.amount === 0) ?? -1
  const zeroIndexOfFixedDeposit = result.fixed?.depositList.findIndex((x) => x.amount === 0) ?? -1

  return [
    ...(result.rate
      ? [
          {
            type: 'line',
            name: '運用できた場合',
            key: 'rateInvestment',
            color: '#EA9C1F',
            dashStyle: 'Solid',
            data: result.rate.investmentList.map((v, idx) => {
              const shouldHide = zeroIndexOfRateInvestment !== -1 && idx > zeroIndexOfRateInvestment
              return {
                x: shouldHide ? idx : v.months / 12,
                y: shouldHide ? null : v.amount,
                custom: { addition: v.addition },
              }
            }),
          },
          {
            type: 'line',
            name: '運用しない場合',
            key: 'rateDeposit',
            color: '#E2CC6F',
            dashStyle: 'Dash',
            data: result.rate.depositList.map((v, idx) => {
              const shouldHide = zeroIndexOfRateDeposit !== -1 && idx > zeroIndexOfRateDeposit
              return {
                x: shouldHide ? idx : v.months / 12,
                y: shouldHide ? null : v.amount,
                custom: { addition: v.addition },
              }
            }),
          },
        ]
      : []),
    ...(result.fixed
      ? [
          {
            type: 'line',
            name: '運用できた場合',
            key: 'fixedInvestment',
            color: '#55A0BF',
            dashStyle: 'Solid',
            data: result.fixed.investmentList.map((v, idx) => {
              const shouldHide =
                zeroIndexOfFixedInvestment !== -1 && idx > zeroIndexOfFixedInvestment
              return {
                x: shouldHide ? idx : v.months / 12,
                y: shouldHide ? null : v.amount,
              }
            }),
          },
          {
            type: 'line',
            name: '運用しない場合',
            key: 'fixedDeposit',
            color: '#6ACEBB',
            dashStyle: 'Dot',
            data: result.fixed.depositList.map((v, idx) => {
              const shouldHide = zeroIndexOfFixedDeposit !== -1 && idx > zeroIndexOfFixedDeposit
              return {
                x: shouldHide ? idx : v.months / 12,
                y: shouldHide ? null : v.amount,
              }
            }),
          },
        ]
      : []),
  ]
}

export const FinancialCalcReversalChart: FC<FinancialCalcReversalChartProps> = React.memo(
  (props) => {
    const result = useSelector((state: AppState) => state.financialCalcReversal.result)
    const detectionWidth = useRef(window.innerWidth)
    const responseCode = useSelector((state: AppState) => state.financialCalcReversal.responseCode)

    const [hover, setHover] = React.useState<HoverPayload | null>(null)
    const [activeKeys, setActiveKeys] = React.useState<Record<string, boolean>>({
      rateInvestment: true,
      rateDeposit: true,
      fixedInvestment: true,
      fixedDeposit: true,
    })

    useEffect(() => {
      function handleResize() {
        setTimeout(() => {
          const newWidth = window.innerWidth
          if (detectionWidth.current !== newWidth) {
            detectionWidth.current = newWidth
            if (result) {
              makeChart(
                chartId,
                makeChartSeries(result),
                result.startAge,
                result.interest,
                props.isResponsive,
                { onHover: setHover }
              )
            }
          }
        }, 500)
      }
      if (result) {
        makeChart(
          chartId,
          makeChartSeries(result),
          result.startAge,
          result.interest,
          props.isResponsive,
          { onHover: setHover }
        )
        if (props.isResponsive) {
          window.addEventListener('resize', handleResize)
          return () => {
            window.removeEventListener('resize', handleResize)
          }
        }
      } else {
        resetChart()
      }
    }, [result, responseCode, props.isResponsive])

    useEffect(() => {
      if (result)
        setActiveKeys(() => ({
          rateInvestment: !!result.rate,
          rateDeposit: !!result.rate,
          fixedInvestment: !!result.fixed,
          fixedDeposit: !!result.fixed,
        }))
    }, [result])

    return (
      <>
        <FinancialCalcReversalResultComponent
          isResponsive={props.isResponsive}
          hover={hover}
          isMonthlyWithdrawal={props.isMonthlyWithdrawal}
          disSavingRate={props.disSavingRate}
          addition={props.addition}
          setHover={setHover}
        />
        <div className="u-mt30px reversal_chart_area">
          {result && (
            <>
              <h3 className="c-heading-1 u-mt63px">保有資産残高の推移</h3>
              <div className="legend_wrapper">
                <dl>
                  <dt>定率</dt>
                  <dd>
                    <label className="teritsu_investment_list">
                      <input
                        type="checkbox"
                        checked={!!activeKeys.rateInvestment}
                        onChange={() => toggleSeriesVisibility('rateInvestment', setActiveKeys)}
                        disabled={!result.rate}
                      />
                      運用できた場合
                    </label>
                    <label className="teritsu_list">
                      <input
                        type="checkbox"
                        checked={!!activeKeys.rateDeposit}
                        onChange={() => toggleSeriesVisibility('rateDeposit', setActiveKeys)}
                        disabled={!result.rate}
                      />
                      運用しない場合
                    </label>
                  </dd>
                </dl>
                <dl>
                  <dt>定額</dt>
                  <dd>
                    <label className="tegaku_investment_list">
                      <input
                        type="checkbox"
                        checked={!!activeKeys.fixedInvestment}
                        onChange={() => toggleSeriesVisibility('fixedInvestment', setActiveKeys)}
                        disabled={!result.fixed}
                      />
                      運用できた場合
                    </label>
                    <label className="tegaku_list">
                      <input
                        type="checkbox"
                        checked={!!activeKeys.fixedDeposit}
                        onChange={() => toggleSeriesVisibility('fixedDeposit', setActiveKeys)}
                        disabled={!result.fixed}
                      />
                      運用しない場合
                    </label>
                  </dd>
                </dl>
              </div>
            </>
          )}
          <div id={chartId} />
          <div className="c-note u-pt60px">
            <ul className="add-style">
              <li>
                {/* eslint-disable-next-line max-len */}
                定率取り崩しは、保有資産を一定の割合で取り崩す方法であるため、資産寿命はシミュレーション結果として保有資産残高が0円となる時点が120歳を超える場合に表示される「120+歳---ヶ月」となるケースが頻出されることが想定されます。
              </li>
              <li>
                定率取り崩しの各時点における取り崩し額および定率・定額取り崩しのそれぞれの保有資産残高は、各年齢の開始時点における直近の値を表示しております。
                シミュレーション結果は、1万円未満を四捨五入して表示をしているため、実際の残高と一致しない場合があります。また、税金・手数料などは考慮しておりません。
              </li>
            </ul>
          </div>
        </div>
      </>
    )
  }
)
